import { useState } from 'react';
import type { ImportReportDto } from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { useCatalogVersions, useCommitImport, useCostCatalogs, usePreviewImport } from './hooks';

/** Reads a File into base64 (no upload library — FileReader only). */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.onload = () => {
      const res = String(reader.result);
      resolve(res.includes(',') ? res.slice(res.indexOf(',') + 1) : res);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * « Import » (ADR-025) : upload d'un fichier XLSX/CSV → **prévisualisation** (rapport lignes valides /
 * invalides / doublons / conflits, SANS écriture) → **commit** transactionnel, activable UNIQUEMENT
 * après une prévisualisation propre. La cible doit être une version BROUILLON (le serveur refuse sinon).
 */
export function ImportTab(): JSX.Element {
  const catalogs = useCostCatalogs();
  const [catalogId, setCatalogId] = useState('');
  const versions = useCatalogVersions(catalogId, catalogId !== '');
  const [targetVersionId, setTargetVersionId] = useState('');
  const [format, setFormat] = useState<'XLSX' | 'CSV'>('XLSX');
  const [fileBase64, setFileBase64] = useState('');
  const [fileName, setFileName] = useState('');
  const [report, setReport] = useState<ImportReportDto | null>(null);

  const preview = usePreviewImport();
  const commit = useCommitImport();

  const clean =
    report !== null &&
    report.invalidRows.length === 0 &&
    report.duplicates.length === 0 &&
    report.conflicts.length === 0;
  const ready = targetVersionId !== '' && fileBase64 !== '';

  const onFile = async (file: File | undefined): Promise<void> => {
    setReport(null);
    if (!file) {
      setFileBase64('');
      setFileName('');
      return;
    }
    setFileName(file.name);
    setFileBase64(await readBase64(file));
  };

  const runPreview = (): void => {
    if (!ready) return;
    preview.mutate({ targetVersionId, format, fileBase64 }, { onSuccess: (r) => setReport(r) });
  };
  const runCommit = (): void => {
    if (!ready || !clean) return;
    commit.mutate({ targetVersionId, format, fileBase64 }, { onSuccess: (r) => setReport(r) });
  };

  const brouillonVersions = (versions.data ?? []).filter((v) => v.status === 'BROUILLON');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import de coûts (XLSX / CSV)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Modèle canonique DERLIZ : colonnes{' '}
          <code>
            country_code;city_code;district_code;
            zone_category_code;component_type_code;unit_code;amount;valid_from;valid_to;source
          </code>
          . L'import ne peut cibler qu'une version <strong>BROUILLON</strong>.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="imp-cat">Catalogue</Label>
            <Select
              id="imp-cat"
              value={catalogId}
              onChange={(e) => {
                setCatalogId(e.target.value);
                setTargetVersionId('');
                setReport(null);
              }}
            >
              <option value="">— Choisir —</option>
              {(catalogs.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="imp-ver">Version cible (BROUILLON)</Label>
            <Select
              id="imp-ver"
              value={targetVersionId}
              onChange={(e) => {
                setTargetVersionId(e.target.value);
                setReport(null);
              }}
              disabled={catalogId === ''}
            >
              <option value="">— Choisir —</option>
              {brouillonVersions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.versionNumber ?? '?'} ({v.validFrom})
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="imp-fmt">Format</Label>
            <Select
              id="imp-fmt"
              value={format}
              onChange={(e) => setFormat(e.target.value as 'XLSX' | 'CSV')}
            >
              <option value="XLSX">XLSX</option>
              <option value="CSV">CSV</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="imp-file">Fichier</Label>
            <input
              id="imp-file"
              type="file"
              accept=".xlsx,.csv"
              className="block w-full text-sm"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            {fileName ? <p className="mt-1 text-xs text-slate-500">{fileName}</p> : null}
          </div>
        </div>

        <div className="flex gap-2">
          <Button type="button" onClick={runPreview} disabled={!ready || preview.isPending}>
            {preview.isPending ? 'Prévisualisation…' : 'Prévisualiser'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={runCommit}
            disabled={!ready || !clean || commit.isPending}
            title={clean ? '' : 'Une prévisualisation sans erreur est requise avant le commit'}
          >
            {commit.isPending ? 'Import…' : 'Committer'}
          </Button>
        </div>

        {preview.isError ? (
          <Alert variant="error" details={toDisplayError(preview.error).details}>
            {toDisplayError(preview.error).message}
          </Alert>
        ) : null}
        {commit.isError ? (
          <Alert variant="error" details={toDisplayError(commit.error).details}>
            {toDisplayError(commit.error).message}
          </Alert>
        ) : null}

        {report ? (
          <div className="space-y-2 rounded-md border border-slate-200 p-3 text-sm">
            <p>
              Total : {report.totalRows} · Valides : {report.validRows.length} · Invalides :{' '}
              <span className={report.invalidRows.length ? 'font-semibold text-rose-600' : ''}>
                {report.invalidRows.length}
              </span>{' '}
              · Doublons : {report.duplicates.length} · Conflits : {report.conflicts.length}
              {report.createdCount !== undefined ? ` · Créées : ${report.createdCount}` : ''}
            </p>
            {report.invalidRows.length > 0 ? (
              <ul className="list-inside list-disc text-rose-700">
                {report.invalidRows.map((r) => (
                  <li key={r.line}>
                    Ligne {r.line} : {r.errors.join(' ; ')}
                  </li>
                ))}
              </ul>
            ) : null}
            {clean && report.createdCount === undefined ? (
              <Alert variant="success">
                Prévisualisation valide — vous pouvez committer l'import.
              </Alert>
            ) : null}
            {report.createdCount !== undefined ? (
              <Alert variant="success">
                Import committé : {report.createdCount} ligne(s) créée(s).
              </Alert>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
